/**
 * ELAN template (.etf) or annotation file (.eaf) → a <VideoTiers> labeling
 * config.
 *
 * A sign-language corpus usually already has its tier structure as an ELAN
 * template. Both file kinds carry the same parts: LINGUISTIC_TYPE (ELAN's
 * tier types, with their CONSTRAINTS stereotype and optional controlled
 * vocabulary / lexicon), TIER (name, type, parent, participant, language)
 * and CONTROLLED_VOCABULARY. Annotations in an .eaf are ignored.
 */

export type ElanImport = {
  config: string;
  tiers: number;
  types: number;
  vocabularies: number;
};

export class ElanTemplateError extends Error {}

const STEREOTYPE: Record<string, string> = {
  time_subdivision: "time_subdivision",
  symbolic_subdivision: "symbolic_subdivision",
  symbolic_association: "symbolic_association",
  included_in: "included_in",
};

const esc = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const attrs = (pairs: [string, string | undefined | null][]) =>
  pairs
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}="${esc(String(v))}"`)
    .join(" ");

/** Values of a controlled vocabulary: ELAN 2.8+ writes CV_ENTRY_ML with one
 *  CVE_VALUE per language (the first is used), older files CV_ENTRY text. */
const vocabularyValues = (cv: Element): string[] => {
  const out: string[] = [];
  for (const ml of Array.from(cv.getElementsByTagName("CV_ENTRY_ML"))) {
    const v = ml.getElementsByTagName("CVE_VALUE")[0]?.textContent?.trim();
    if (v) out.push(v);
  }
  if (!out.length) {
    for (const e of Array.from(cv.getElementsByTagName("CV_ENTRY"))) {
      const v = e.textContent?.trim();
      if (v) out.push(v);
    }
  }
  return Array.from(new Set(out));
};

export function elanTemplateToConfig(xml: string, opts: { name?: string; value?: string } = {}): ElanImport {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) {
    throw new ElanTemplateError("This file is not valid XML, so it is not an ELAN template.");
  }
  const root = doc.documentElement;
  if (!root || root.tagName !== "ANNOTATION_DOCUMENT") {
    throw new ElanTemplateError("This is not an ELAN file: it has no ANNOTATION_DOCUMENT.");
  }
  const direct = (tag: string) => Array.from(root.children).filter((el) => el.tagName === tag);

  const vocabularies = new Map<string, string[]>();
  for (const cv of direct("CONTROLLED_VOCABULARY")) {
    const id = cv.getAttribute("CV_ID");
    if (!id) continue;
    // An external CV (EXT_REF) has its entries elsewhere; keep what is inline.
    vocabularies.set(id, vocabularyValues(cv));
  }

  const types = new Map<string, { stereotype: string; vocabulary: string; lexicon: boolean }>();
  for (const lt of direct("LINGUISTIC_TYPE")) {
    const id = lt.getAttribute("LINGUISTIC_TYPE_ID");
    if (!id) continue;
    const constraint = (lt.getAttribute("CONSTRAINTS") || "").toLowerCase();
    const cvRef = lt.getAttribute("CONTROLLED_VOCABULARY_REF") || "";
    types.set(id, {
      stereotype: STEREOTYPE[constraint] || "none",
      vocabulary: cvRef && vocabularies.get(cvRef)?.length ? cvRef : "",
      lexicon: !!lt.getAttribute("LEXICON_REF"),
    });
  }

  const tiers = direct("TIER")
    .map((t) => ({
      name: t.getAttribute("TIER_ID") || "",
      type: t.getAttribute("LINGUISTIC_TYPE_REF") || "",
      parent: t.getAttribute("PARENT_REF") || "",
      participant: t.getAttribute("PARTICIPANT") || "",
      lang: t.getAttribute("LANG_REF") || "",
    }))
    .filter((t) => t.name);
  if (!tiers.length) throw new ElanTemplateError("The file declares no tiers.");

  // A tier whose type is not declared still needs one: make it independent
  // or dependent to match where it sits.
  for (const t of tiers) {
    if (!types.has(t.type)) {
      const fallback = t.parent ? "dependent" : "default";
      if (!types.has(fallback)) {
        types.set(fallback, { stereotype: t.parent ? "included_in" : "none", vocabulary: "", lexicon: false });
      }
      t.type = fallback;
    }
  }

  const usedVocabs = new Set([...types.values()].map((t) => t.vocabulary).filter(Boolean));
  const lines = [
    "<View>",
    `  <VideoTiers ${attrs([
      ["name", opts.name || "tiers"],
      ["value", opts.value || "$video"],
    ])}>`,
  ];
  for (const [name, t] of types) {
    lines.push(
      `    <TierType ${attrs([
        ["value", name],
        ["stereotype", t.stereotype],
        ["vocabulary", t.vocabulary],
        ["lexicon", t.lexicon ? "true" : ""],
      ])}/>`,
    );
  }
  for (const id of usedVocabs) {
    lines.push(`    <Vocabulary ${attrs([["value", id]])}>`);
    for (const v of vocabularies.get(id) || []) lines.push(`      <Entry ${attrs([["value", v]])}/>`);
    lines.push("    </Vocabulary>");
  }
  for (const t of tiers) {
    lines.push(
      `    <Tier ${attrs([
        ["value", t.name],
        ["tierType", t.type],
        ["parent", t.parent],
        ["participant", t.participant],
        ["lang", t.lang],
      ])}/>`,
    );
  }
  lines.push("  </VideoTiers>", "</View>");
  return { config: lines.join("\n"), tiers: tiers.length, types: types.size, vocabularies: usedVocabs.size };
}
